import React, { useState, useMemo } from 'react';
import type { BusinessHours } from '../../types/location.types';
import './StoreOpeningHours.css';

interface StoreOpeningHoursProps {
  businessHours?: BusinessHours[];
  variant?: 'default' | 'compact'; // compact for LocationOverlay
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SHORT_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatTime = (time: string): string => {
  if (!time) return '';
  // Convert "09:00:00" or "09:00" to "9:00 AM"
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const StoreOpeningHours: React.FC<StoreOpeningHoursProps> = ({
  businessHours,
  variant = 'default'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { isOpen, statusText } = useMemo(() => {
    if (!businessHours || businessHours.length === 0) {
      return { isOpen: false, currentDayHours: null, statusText: 'Hours not available' };
    }

    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Minutes since midnight

    // Find today's hours
    const todayHours = businessHours.find(bh => bh.dayOfWeek === currentDay);

    if (!todayHours || todayHours.isClosedAllDay) {
      return { isOpen: false, currentDayHours: todayHours, statusText: 'Closed today' };
    }

    if (todayHours.isOpen24Hours) {
      return { isOpen: true, currentDayHours: todayHours, statusText: 'Open 24 hours' };
    }

    // Parse opening and closing times
    const [openHour, openMin] = todayHours.openingTime.split(':').map(Number);
    const [closeHour, closeMin] = todayHours.closingTime.split(':').map(Number);
    const openingMinutes = openHour * 60 + openMin;
    const closingMinutes = closeHour * 60 + closeMin;

    const isCurrentlyOpen = currentTime >= openingMinutes && currentTime < closingMinutes;

    if (isCurrentlyOpen) {
      return {
        isOpen: true,
        currentDayHours: todayHours,
        statusText: `Open until ${formatTime(todayHours.closingTime)}`
      };
    } else if (currentTime < openingMinutes) {
      return {
        isOpen: false,
        currentDayHours: todayHours,
        statusText: `Opens at ${formatTime(todayHours.openingTime)}`
      };
    } else {
      return {
        isOpen: false,
        currentDayHours: todayHours,
        statusText: 'Closed'
      };
    }
  }, [businessHours]);

  // Sort and deduplicate business hours by day (Monday first)
  const sortedHours = useMemo(() => {
    if (!businessHours) return [];

    // Deduplicate by dayOfWeek (keep first occurrence)
    const uniqueByDay = new Map<number, BusinessHours>();
    businessHours.forEach(bh => {
      if (!uniqueByDay.has(bh.dayOfWeek)) {
        uniqueByDay.set(bh.dayOfWeek, bh);
      }
    });

    // Sort: Monday (1) first, then Tuesday (2), etc., Sunday (0) last
    return Array.from(uniqueByDay.values()).sort((a, b) => {
      const dayA = a.dayOfWeek === 0 ? 7 : a.dayOfWeek;
      const dayB = b.dayOfWeek === 0 ? 7 : b.dayOfWeek;
      return dayA - dayB;
    });
  }, [businessHours]);

  if (!businessHours || businessHours.length === 0) {
    return null;
  }

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`store-opening-hours ${variant}`}>
      <button
        className="opening-hours-toggle"
        onClick={toggleExpanded}
        type="button"
      >
        <span className={`status-indicator ${isOpen ? 'open' : 'closed'}`} />
        <span className="status-text">{statusText}</span>
        <svg
          className={`chevron ${isExpanded ? 'expanded' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isExpanded && (
        <div className="opening-hours-dropdown">
          {sortedHours.map((hours, index) => {
            const dayName = variant === 'compact'
              ? SHORT_DAY_NAMES[hours.dayOfWeek]
              : DAY_NAMES[hours.dayOfWeek];
            const isToday = hours.dayOfWeek === new Date().getDay();

            return (
              <div
                key={`day-${hours.dayOfWeek}-${index}`}
                className={`hours-row ${isToday ? 'today' : ''}`}
              >
                <span className="day-name">{dayName}</span>
                <span className="hours-value">
                  {hours.isClosedAllDay
                    ? 'Closed'
                    : hours.isOpen24Hours
                      ? '24 hours'
                      : `${formatTime(hours.openingTime)} - ${formatTime(hours.closingTime)}`
                  }
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StoreOpeningHours;
